'use client';

import React, { useState } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface LogEntry {
  id: string;
  postTitle: string;
  platform: string;
  action: string;
  status: 'erfolg' | 'fehler' | 'ausstehend' | 'geplant';
  timestamp: string;
  detail?: string;
}

const logEntries: LogEntry[] = [
  { id: 'log-001', postTitle: 'KI im Mittelstand: 5 Chancen', platform: 'LI', action: 'Veröffentlicht', status: 'erfolg', timestamp: '02.04.2026 · 09:00', detail: 'Direkt veröffentlicht via LinkedIn API' },
  { id: 'log-002', postTitle: 'Hinter den Kulissen: Unser Alltag', platform: 'IG', action: 'Veröffentlicht', status: 'erfolg', timestamp: '02.04.2026 · 09:05', detail: 'Caption + Bild-Platzhalter übertragen' },
  { id: 'log-003', postTitle: 'Das Meeting-Bingo', platform: 'FB', action: 'Fehler', status: 'fehler', timestamp: '02.04.2026 · 09:10', detail: 'Facebook Token abgelaufen — Reconnect erforderlich' },
  { id: 'log-004', postTitle: '5 Fehler bei Content-Planung', platform: 'X', action: 'Geplant', status: 'geplant', timestamp: '07.04.2026 · 08:00', detail: 'Geplant für 08:00 Uhr' },
  { id: 'log-005', postTitle: 'Remote-Work 2026: Die Wahrheit', platform: 'X', action: 'Geplant', status: 'geplant', timestamp: '09.04.2026 · 12:00', detail: 'Geplant für 12:00 Uhr' },
  { id: 'log-006', postTitle: 'Case Study: Müller & Söhne', platform: 'LI', action: 'Veröffentlicht', status: 'erfolg', timestamp: '01.04.2026 · 10:30', detail: 'Artikel mit 1.240 Wörtern übertragen' },
  { id: 'log-007', postTitle: 'Produktneuheit Q2', platform: 'IG', action: 'Ausstehend', status: 'ausstehend', timestamp: '—', detail: 'Wartet auf Freigabe' },
  { id: 'log-008', postTitle: 'ZIP-Export Q2-Kampagne', platform: 'LOCAL', action: 'Heruntergeladen', status: 'erfolg', timestamp: '02.04.2026 · 14:55', detail: '34 Beiträge · 2.4 MB ZIP' },
  { id: 'log-009', postTitle: 'Google Drive Sync', platform: 'DRIVE', action: 'Synchronisiert', status: 'erfolg', timestamp: '02.04.2026 · 14:56', detail: '34 Dateien → /SEARCH4-DISTORTION/Berger-GmbH/' },
];

const statusIcon: Record<string, React.ReactNode> = {
  erfolg: <CheckCircle size={13} className="text-green-400 flex-shrink-0" />,
  fehler: <XCircle size={13} className="text-red-400 flex-shrink-0" />,
  ausstehend: <Clock size={13} className="text-yellow-400 flex-shrink-0" />,
  geplant: <AlertCircle size={13} className="text-blue-400 flex-shrink-0" />,
};

const platformBadge: Record<string, string> = {
  LI: 'platform-badge-li',
  IG: 'platform-badge-ig',
  X: 'platform-badge-x',
  FB: 'platform-badge-fb',
  LOCAL: 'bg-surface-2 text-muted border-border',
  DRIVE: 'bg-green-950/20 text-green-400 border-green-800/30',
};

export default function ExportPublishLog() {
  const [filter, setFilter] = useState<string>('alle');

  const filters = ['alle', 'erfolg', 'fehler', 'ausstehend', 'geplant'];

  const filtered = logEntries.filter((e) => filter === 'alle' || e.status === filter);

  return (
    <div className="punk-card">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <p className="section-label">Aktivitätsprotokoll</p>
          <h3 className="text-sm font-600 text-white mt-1">Publish & Export Verlauf</h3>
        </div>
        <button
          onClick={() => toast.info('Protokoll wird aktualisiert…')}
          className="btn-ghost py-1.5 px-3 text-xs gap-1.5"
        >
          <RefreshCw size={12} />
          Aktualisieren
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 px-5 py-3 border-b border-border overflow-x-auto">
        {filters.map((f) => (
          <button
            key={`log-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`text-xs mono px-3 py-1.5 rounded border flex-shrink-0 transition-all ${
              filter === f
                ? 'border-accent text-accent bg-accent/8' :'border-border text-muted hover:border-accent/40 hover:text-white'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'alle' && (
              <span className="ml-1.5 bg-surface-2 rounded px-1">
                {logEntries.filter((e) => e.status === f).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Log entries */}
      <div className="divide-y divide-border/50">
        {filtered.map((entry) => (
          <div key={entry.id} className="flex items-start gap-4 p-4 hover:bg-surface transition-colors group">
            <div className="mt-0.5">{statusIcon[entry.status]}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-500 text-white truncate max-w-xs">{entry.postTitle}</span>
                <span className={`status-badge border flex-shrink-0 ${platformBadge[entry.platform] ?? ''}`}>
                  {entry.platform}
                </span>
                <span className="text-xs text-muted">{entry.action}</span>
              </div>
              {entry.detail && (
                <p className="text-xs text-muted leading-relaxed">{entry.detail}</p>
              )}
            </div>
            <div className="flex-shrink-0 text-right">
              <span className="mono text-xs text-muted whitespace-nowrap">{entry.timestamp}</span>
              {entry.status === 'fehler' && (
                <button
                  onClick={() => toast.info(`Erneuter Versuch: ${entry.postTitle}`)}
                  className="block mt-1 text-xs mono text-red-400 hover:text-red-300 transition-colors"
                >
                  Erneut versuchen
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="p-10 text-center">
          <Clock size={24} className="text-muted mx-auto mb-3" />
          <p className="text-sm font-500 text-white">Keine Einträge</p>
          <p className="text-xs text-muted mt-1">Keine Aktivitäten mit dem Filter &quot;{filter}&quot;</p>
        </div>
      )}

      <div className="px-5 py-3 border-t border-border flex items-center justify-between">
        <span className="mono text-xs text-muted">{filtered.length} von {logEntries.length} Einträgen</span>
        <button className="text-xs mono text-muted hover:text-accent transition-colors">
          Vollständiges Protokoll exportieren →
        </button>
      </div>
    </div>
  );
}